import { useEffect } from "react";
import "./styles.scss";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

const businessInfo = {
  brandName: "BR&V Technology Corp.",
  registration: "SEC Registration Number: 2023090119006-01",
  taxRegistration: "BIR TIN: 649-694-887-00000",
  birCertificate: "BIR Certificate of Registration OCN: 077RC2024000006632",
  mayorPermit: "Mayor's Permit No.: 2026-R-0001023; Account No.: B-00449",
  address: "Lot 27 Blk 17, Canon St., Town and Country Cambridge, Brgy. Zone 15, Talisay City, Negros Occidental 6115, Philippines",
  email: "contact@brvtechnologysg.com",
  phone: "+63 994 090 8740",
};

const pages = {
  terms: {
    eyebrow: "Terms",
    title: "Terms and Conditions",
    metaDescription: "Review BR&V Technology Corp. terms for website use, software products, implementation work, support, and digital services.",
    lead: "These terms govern access to BR&V Technology websites, software products, demos, implementation work, support, and related digital services.",
    sections: [
      {
        title: "Business Information",
        body: [
          `${businessInfo.brandName} is a corporation duly registered in the Philippines.`,
          businessInfo.registration,
          businessInfo.taxRegistration,
          businessInfo.birCertificate,
          businessInfo.mayorPermit,
          `Business address: ${businessInfo.address}.`,
        ],
      },
      {
        title: "Use of the Website and Services",
        body: [
          "You may use the website and services only for lawful business purposes. You must not attempt to disrupt the site, misuse forms, reverse engineer restricted software, or access accounts or systems without authorization.",
          "Product descriptions, demos, pricing discussions, and proposals are provided for evaluation and planning. Final scope, fees, service levels, delivery timelines, and support commitments are governed by the signed proposal, subscription order, statement of work, or service agreement accepted by BR&V Technology.",
        ],
      },
      {
        title: "Accounts, Data, and Security",
        body: [
          "Customers are responsible for keeping account credentials confidential and for the accuracy of business data submitted to BR&V Technology systems.",
          "BR&V Technology applies reasonable safeguards for hosted systems and customer data. No internet-delivered service can be guaranteed to be uninterrupted, error-free, or immune from every security risk.",
        ],
      },
      {
        title: "Fees, Billing, and Intellectual Property",
        body: [
          "Fees, payment schedules, taxes, renewals, and cancellation rules are stated in the applicable commercial agreement or invoice.",
          "BR&V Technology retains ownership of its software, templates, documentation, trademarks, designs, and pre-existing technology. Customers retain ownership of their own business records and data, subject to the rights needed for BR&V Technology to provide contracted services.",
        ],
      },
      {
        title: "Limitations and Changes",
        body: [
          "To the fullest extent permitted by law, BR&V Technology is not liable for indirect, incidental, consequential, punitive, or special damages arising from use of the website or services.",
          "BR&V Technology may update these terms when business, legal, technical, or service requirements change. Continued use of the website or services after an update means the updated terms apply.",
        ],
      },
    ],
  },
  privacy: {
    eyebrow: "Privacy",
    title: "Privacy Policy",
    metaDescription: "Learn how BR&V Technology Corp. collects, uses, stores, and protects personal and business information.",
    lead: "This policy explains how BR&V Technology collects, uses, stores, and protects personal and business information submitted through its website and services.",
    sections: [
      {
        title: "Information We Collect",
        body: [
          "We may collect contact details, company details, inquiry messages, demo requests, account information, billing records, support tickets, device and usage data, and information customers choose to upload into BR&V Technology systems.",
          "We may also collect limited technical data through cookies, analytics, logs, and similar tools to operate, secure, and improve the website and services.",
        ],
      },
      {
        title: "How We Use Information",
        body: [
          "We use information to respond to inquiries, provide demos, prepare proposals, deliver software and consulting services, operate accounts, process billing, provide customer support, improve products, protect systems, and comply with legal obligations.",
          "We do not sell personal information. We may share information with service providers, hosting providers, payment processors, professional advisers, regulators, or authorities when required for operations, compliance, dispute handling, or legal process.",
        ],
      },
      {
        title: "Retention and Security",
        body: [
          "We keep information only as long as needed for the purposes described in this policy, for active customer relationships, backups, audit logs, dispute handling, and legal or accounting requirements.",
          "We use reasonable administrative, technical, and organizational safeguards to protect information. Customers should also use strong passwords, access controls, and appropriate internal data governance.",
        ],
      },
      {
        title: "Your Choices and Requests",
        body: [
          "You may request access, correction, deletion, restriction, or review of personal information by contacting customer service, subject to identity verification and applicable legal limits.",
          "You may also ask questions about privacy practices, cookies, account data, and vendor processing using the contact details on this page.",
        ],
      },
    ],
  },
  returns: {
    eyebrow: "Refunds",
    title: "Return and Refund Policy",
    metaDescription: "Review BR&V Technology Corp. refund guidelines for software subscriptions, implementation, support, and consulting services.",
    lead: "BR&V Technology provides digital software, subscriptions, support, implementation, and consulting services. Physical product returns generally do not apply unless a separate written agreement says otherwise.",
    sections: [
      {
        title: "Digital Services and Subscriptions",
        body: [
          "Subscription fees, setup fees, implementation fees, support fees, customization work, training, and consulting services are governed by the accepted proposal, invoice, service agreement, or statement of work.",
          "Because services are digital and work may begin immediately after approval, completed work, activated subscriptions, delivered customizations, third-party fees, and consumed support hours are generally non-refundable.",
        ],
      },
      {
        title: "Refund Review",
        body: [
          "BR&V Technology may review refund requests for duplicate payments, incorrect charges, billing errors, cancellations accepted before work begins, or material service failures caused solely by BR&V Technology that cannot be reasonably remedied.",
          "Approved refunds are returned through the original payment channel when practical. Processing timelines may depend on banks, payment providers, and required verification.",
        ],
      },
      {
        title: "How to Request a Refund",
        body: [
          `Send the request to ${businessInfo.email} with the customer name, invoice number, payment date, product or service involved, reason for the request, and supporting documents.`,
          "Refund requests should be submitted as soon as the issue is discovered so BR&V Technology can investigate, correct billing errors, or provide a service remedy.",
        ],
      },
    ],
  },
  dispute: {
    eyebrow: "Resolution",
    title: "Dispute Resolution Policy",
    metaDescription: "Review how BR&V Technology Corp. handles customer service, billing, privacy, and contract dispute concerns.",
    lead: "BR&V Technology encourages customers to raise service, billing, privacy, or contract issues early so they can be reviewed and resolved through a documented process.",
    sections: [
      {
        title: "Step 1: Contact Customer Service",
        body: [
          `Send the concern to ${businessInfo.email} or call ${businessInfo.phone}. Include the account or invoice number, a summary of the concern, relevant dates, screenshots or documents, and the outcome requested.`,
          "BR&V Technology will acknowledge the concern and may request additional information needed to review the issue.",
        ],
      },
      {
        title: "Step 2: Internal Review",
        body: [
          "If the issue cannot be resolved by front-line support, it will be escalated for management review. BR&V Technology will review the agreement, service records, billing history, communications, and technical evidence where relevant.",
          "The company will aim to provide a practical resolution, correction, service remedy, refund decision, or written explanation within a reasonable period based on the complexity of the matter.",
        ],
      },
      {
        title: "Step 3: Further Remedies",
        body: [
          "If the parties cannot resolve the dispute through customer service and internal review, the parties may agree to mediation or another mutually acceptable dispute resolution process.",
          "Nothing in this policy prevents either party from seeking remedies available under applicable law or a signed agreement.",
        ],
      },
    ],
  },
};

const aliases = {
  "terms-and-conditions": "terms",
  "privacy-policy": "privacy",
  "return-and-refund-policy": "returns",
  "dispute-resolution-policy": "dispute",
};

const ContactDetail = ({ icon: Icon, label, children }) => (
  <div>
    <Icon className="contactIcon" fontSize="inherit" aria-hidden="true" />
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

export default function LegalPage({ type }) {
  const page = pages[aliases[type] || type] || pages.terms;

  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute("content");

    document.title = `${page.title} | BR&V Technology Corp.`;
    metaDescription?.setAttribute("content", page.metaDescription || page.lead);

    return () => {
      document.title = previousTitle;
      if (metaDescription && previousDescription) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, [page]);

  return (
    <main id="legalPage">
      <section className="legalHero">
        <div className="inner-container">
          <span className="eyebrow">{page.eyebrow}</span>
          <h1>{page.title}</h1>
          <p>{page.lead}</p>
        </div>
      </section>

      <section className="legalContent">
        <div className="inner-container legalGrid">
          <aside className="legalContact" aria-label="Customer service contact information">
            <h2>Customer Service</h2>
            <dl>
              <ContactDetail icon={EmailOutlinedIcon} label="Email">
                <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>
              </ContactDetail>
              <ContactDetail icon={PhoneOutlinedIcon} label="Phone">
                <a href={`tel:${businessInfo.phone.replace(/\s/g, "")}`}>{businessInfo.phone}</a>
              </ContactDetail>
              <ContactDetail icon={LocationOnOutlinedIcon} label="Business Address">
                {businessInfo.address}
              </ContactDetail>
              <ContactDetail icon={BadgeOutlinedIcon} label="Registration">
                {businessInfo.registration}
              </ContactDetail>
              <ContactDetail icon={ReceiptLongOutlinedIcon} label="Tax Registration">
                {businessInfo.taxRegistration}
              </ContactDetail>
              <ContactDetail icon={AssignmentTurnedInOutlinedIcon} label="Local Permit">
                {businessInfo.mayorPermit}
              </ContactDetail>
            </dl>
          </aside>

          <div className="legalSections">
            {page.sections.map((section) => (
              <article key={section.title}>
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </article>
            ))}
            <div className="legalDisclaimer">
              <strong>Policy Note</strong>
              <p>
                These pages summarize BR&V Technology Corp. business policies for website and service use.
                They are not legal advice and may be updated as business, legal, technical, or service requirements change.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
